import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  CheckCircle2, Bike, Home, ArrowRight, Phone, MapPin,
  LogOut, RadioTower, ChevronRight, Wallet, User, Save,
} from "lucide-react";
import { toast } from "sonner";
import DateRangeFilter from "@/components/DateRangeFilter";
import OrderDetailDialog from "@/components/OrderDetailDialog";

const LABELS = {
  received: "Awaiting rider",
  assigned: "Presa in carico",
  "on-the-way": "In consegna",
  delivered: "Consegnata",
};
const ICONS = { received: CheckCircle2, assigned: Bike, "on-the-way": Bike, delivered: Home };

const RiderProfile = () => {
  const [me, setMe] = useState(null);
  const [prefix, setPrefix] = useState("+34");
  const [local, setLocal] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/rider/me");
      setMe(r.data);
      const p = (r.data?.phone || "").trim();
      const m = p.match(/^(\+\d{1,3})(.*)$/);
      if (m) { setPrefix(m[1]); setLocal(m[2].trim()); }
      else { setLocal(p); }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleOnline = async (v) => {
    try {
      const r = await api.patch("/rider/me", { is_online: v });
      setMe(r.data);
      toast.success(v ? "Sei online e ricevi ordini via WhatsApp" : "Sei offline");
    } catch { toast.error("Errore aggiornamento"); }
  };

  const savePhone = async () => {
    setSaving(true);
    try {
      const phone = `${prefix.trim()}${local.trim().replace(/^\+?\d{0,3}\s?/, "")}`;
      const r = await api.patch("/rider/me", { phone });
      setMe(r.data);
      toast.success("Telefono aggiornato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  if (!me) return null;
  return (
    <div className="bg-white rounded-2xl border border-navy/10 p-4 space-y-3" data-testid="rider-profile-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-navy/60" />
          <span className="font-heading font-bold text-navy text-sm">Il mio profilo</span>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className={`text-xs font-bold ${me.is_online ? "text-teal" : "text-navy/50"}`}>
            {me.is_online ? "● Online" : "○ Offline"}
          </span>
          <Switch checked={!!me.is_online} onCheckedChange={toggleOnline} data-testid="rider-online-toggle" />
        </label>
      </div>
      <div>
        <Label className="text-xs">Numero WhatsApp (con prefisso internazionale)</Label>
        <div className="flex gap-2 mt-1">
          <Input
            value={prefix}
            onChange={e => setPrefix(e.target.value)}
            className="w-24 font-mono"
            placeholder="+34"
            data-testid="rider-phone-prefix"
          />
          <Input
            value={local}
            onChange={e => setLocal(e.target.value)}
            className="flex-1 font-mono"
            placeholder="612345678"
            data-testid="rider-phone-local"
          />
          <Button onClick={savePhone} disabled={saving} className="btn-coral" data-testid="rider-phone-save">
            <Save className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-[11px] text-navy/50 mt-1">Riceverai i nuovi ordini via WhatsApp quando sei online.</p>
      </div>
    </div>
  );
};

const RiderEarnings = ({ dateRange }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/rider/earnings", { params: dateRange }).then(r => setData(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [dateRange.start, dateRange.end]);
  if (!data) return null;
  const showRange = data.range_start || data.range_end;
  return (
    <div className="bg-teal/10 border-2 border-teal/30 rounded-2xl p-4 flex items-center gap-4" data-testid="rider-earnings-card">
      <div className="w-12 h-12 rounded-full bg-teal text-white flex items-center justify-center shrink-0">
        <Wallet className="w-6 h-6" />
      </div>
      <div className="flex-1 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase text-teal font-bold tracking-wider">Oggi</div>
          <div className="font-heading text-2xl font-extrabold text-teal" data-testid="rider-today-total">€{data.today_total.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-navy/50 font-bold tracking-wider">{showRange ? "Nel periodo" : "Totale"}</div>
          <div className="font-heading text-2xl font-extrabold text-navy" data-testid="rider-range-total">€{data.all_time_total.toFixed(2)}</div>
          <div className="text-[10px] text-navy/50">{data.orders_delivered} consegne</div>
        </div>
      </div>
    </div>
  );
};

const RiderList = () => {
  const { admin, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [detailOrder, setDetailOrder] = useState(null);
  const prevCountRef = useRef(null);

  const load = useCallback(() => {
    api.get("/rider/orders", { params: dateRange })
      .then((r) => {
        const active = r.data.filter((o) => o.status !== "delivered");
        if (prevCountRef.current !== null && active.length > prevCountRef.current) {
          toast.success("Nuova consegna assegnata");
        }
        prevCountRef.current = active.length;
        setOrders(r.data);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [dateRange.start, dateRange.end]);

  useEffect(() => { load(); const iv = setInterval(load, 8000); return () => clearInterval(iv); }, [load]);

  const active = orders.filter((o) => o.status !== "delivered");
  const done = orders.filter((o) => o.status === "delivered");

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 pb-16 space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-ocean font-bold">Rider dashboard</div>
          <h1 className="font-heading text-3xl font-extrabold text-navy tracking-tight mt-1">Ciao {admin?.name || admin?.email}</h1>
          <p className="text-navy/60 text-sm mt-1 flex items-center gap-1"><RadioTower className="w-3 h-3 animate-pulse text-teal" /> Live — aggiornamento ogni 8 s</p>
        </div>
        <Button
          variant="outline"
          onClick={() => { logout(); nav("/rider/login"); }}
          className="rounded-full border-2 border-navy text-navy hover:bg-navy hover:text-white"
          data-testid="rider-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-1" /> Log out
        </Button>
      </div>

      <RiderProfile />
      <div>
        <DateRangeFilter
          start={dateRange.start}
          end={dateRange.end}
          onChange={setDateRange}
          testIdPrefix="rider-range"
          label="Periodo guadagni & ordini"
        />
      </div>
      <RiderEarnings dateRange={dateRange} />

      <div>
        <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-navy/60 mb-3">Consegne attive ({active.length})</h2>
        {active.length === 0 && loaded && (
          <div className="bg-white rounded-2xl border border-navy/10 p-8 text-center">
            <Bike className="w-8 h-8 text-navy/20 mx-auto mb-2" />
            <p className="text-navy/50 text-sm">Nessuna consegna attiva. Sarai avvisato via WhatsApp quando arriva un nuovo ordine.</p>
          </div>
        )}
        <div className="grid gap-3">
          {active.map((o) => {
            const Icon = ICONS[o.status] || CheckCircle2;
            return (
              <Link
                key={o.order_number}
                to={`/rider/${o.order_number}`}
                className="bg-white rounded-2xl border border-navy/10 p-4 flex items-center gap-3 card-hover"
                data-testid={`rider-active-${o.order_number}`}
              >
                <div className="w-11 h-11 rounded-xl bg-coral/15 flex items-center justify-center shrink-0 text-coral">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-navy">#{o.order_number} <span className="text-navy/50 text-xs font-normal">— {o.restaurant_name}</span></div>
                  <div className="text-xs text-navy/60 mt-0.5 truncate">{LABELS[o.status]}</div>
                  <div className="text-[11px] text-navy/50 mt-0.5" data-testid={`rider-active-date-${o.order_number}`}>Ordinato il {new Date(o.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                  <div className="text-xs text-navy/50 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.delivery?.accommodation_type} — {o.delivery?.address}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-navy/30 shrink-0" />
              </Link>
            );
          })}
        </div>
      </div>

      {done.length > 0 && (
        <div>
          <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-navy/60 mb-3">Completate oggi ({done.length})</h2>
          <div className="grid gap-2">
            {done.slice(0, 10).map((o) => (
              <button
                key={o.order_number}
                type="button"
                onClick={() => setDetailOrder(o)}
                className="bg-sand-50 rounded-xl p-3 flex items-center gap-2 text-sm text-left w-full hover:bg-sand-100 hover:ring-1 hover:ring-teal/30 transition"
                data-testid={`rider-done-row-${o.order_number}`}
              >
                <CheckCircle2 className="w-4 h-4 text-teal shrink-0" />
                <span className="font-mono text-xs">#{o.order_number}</span>
                <span className="text-navy/60 truncate flex-1">{o.restaurant_name} → {o.delivery?.address}</span>
                <span className="text-[11px] text-navy/50 font-mono whitespace-nowrap" data-testid={`rider-done-time-${o.order_number}`}>{new Date(o.delivered_at || o.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
                {o.rider_commission_amount ? <span className="text-teal font-bold text-xs">+€{o.rider_commission_amount.toFixed(2)}</span> : null}
              </button>
            ))}
          </div>
        </div>
      )}

      <OrderDetailDialog
        order={detailOrder}
        open={!!detailOrder}
        onOpenChange={(v) => !v && setDetailOrder(null)}
        showFinancials={false}
        showRiderPayout
      />
    </div>
  );
};

const RiderDetail = ({ orderNumber }) => {
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notAllowed, setNotAllowed] = useState(false);

  const load = () =>
    api.get(`/rider/orders/${orderNumber}`)
      .then((r) => { setOrder(r.data); setNotAllowed(false); })
      .catch((e) => { if (e.response?.status === 403 || e.response?.status === 404) setNotAllowed(true); });

  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [orderNumber]);

  const advance = async (nextStatus) => {
    if (nextStatus === "delivered" && !window.confirm("Confermi la consegna? Il pagamento sarà segnato come completato.")) return;
    setBusy(true);
    try {
      const r = await api.patch(`/rider/orders/${orderNumber}/status`, { status: nextStatus });
      setOrder(r.data);
      toast.success(nextStatus === "on-the-way" ? "Segnato come In consegna" : "Consegna completata 🎉");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  if (notAllowed) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <p className="text-navy/70">Questo ordine non è assegnato a te.</p>
        <Button onClick={() => nav("/rider")} className="btn-coral mt-4">Torna al pannello</Button>
      </div>
    );
  }
  if (!order) return <div className="min-h-[50vh] flex items-center justify-center text-navy/50">Caricamento…</div>;

  const cur = order.status;
  const isFinal = cur === "delivered";
  const CurIcon = ICONS[cur] || CheckCircle2;
  const nextStatus = cur === "assigned" ? "on-the-way" : cur === "on-the-way" ? "delivered" : null;
  const nextLabel = nextStatus === "on-the-way" ? "In consegna" : nextStatus === "delivered" ? "Consegnata" : "";

  const subtotal = order.subtotal || 0;
  const fixed = order.rider_commission_fixed ?? 2.5;
  const pct = order.rider_commission_pct ?? 0.03;
  const payoutPreview = order.rider_commission_amount || +(fixed + subtotal * pct).toFixed(2);

  return (
    <div className="max-w-lg mx-auto px-4 py-8 pb-28">
      <button onClick={() => nav("/rider")} className="text-xs text-navy/60 hover:text-coral flex items-center gap-1 mb-2" data-testid="rider-back">
        ← Indietro
      </button>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-ocean font-bold">Rider view</div>
          <div className="font-heading text-3xl font-extrabold text-navy tracking-tight">#{order.order_number}</div>
          <p className="text-navy/60 text-sm">{order.restaurant_name}</p>
          <p className="text-[11px] text-navy/50 mt-1" data-testid="rider-detail-timestamps">
            Ordinato il <span className="font-semibold text-navy">{new Date(order.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}</span>
            {order.accepted_at && <> · Accettato alle <span className="font-semibold text-navy">{new Date(order.accepted_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span></>}
            {order.delivered_at && <> · Consegnato alle <span className="font-semibold text-teal">{new Date(order.delivered_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span></>}
          </p>
        </div>
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isFinal ? "bg-teal/20 text-teal" : "bg-coral/15 text-coral"}`}>
          <CurIcon className="w-7 h-7" />
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-navy/10 p-5">
        <div className="text-xs uppercase tracking-wider text-navy/50">Stato attuale</div>
        <div className="font-heading text-2xl font-extrabold text-navy mt-1" data-testid="rider-current-status">{LABELS[cur] || cur}</div>
        <div className="mt-4 grid grid-cols-3 gap-1">
          {["assigned", "on-the-way", "delivered"].map((s, i) => {
            const idx = ["assigned", "on-the-way", "delivered"].indexOf(cur);
            return <div key={s} className={`h-1.5 rounded-full ${i <= idx ? "bg-coral" : "bg-navy/10"}`} />;
          })}
        </div>
      </div>

      <div className="mt-4 bg-teal/10 border-2 border-teal/30 rounded-2xl p-4 flex items-center gap-3" data-testid="rider-order-payout">
        <Wallet className="w-6 h-6 text-teal shrink-0" />
        <div className="flex-1">
          <div className="text-[11px] uppercase text-teal font-bold tracking-wider">Il tuo compenso</div>
          <div className="text-xs text-navy/60">€{fixed.toFixed(2)} fisso + {(pct * 100).toFixed(0)}% su €{subtotal.toFixed(2)} prodotti</div>
        </div>
        <div className="font-heading text-2xl font-extrabold text-teal">€{payoutPreview.toFixed(2)}</div>
      </div>

      <div className="mt-4 bg-white rounded-2xl border border-navy/10 p-5 space-y-2">
        <div className="text-xs uppercase tracking-wider text-navy/50">Consegna a</div>
        <div className="font-heading font-bold text-navy text-lg">{order.customer?.first_name} {order.customer?.last_name}</div>
        <a href={`tel:${order.customer?.phone}`} className="flex items-center gap-2 text-navy hover:text-coral">
          <Phone className="w-4 h-4" /> {order.customer?.phone}
        </a>
        <div className="flex items-start gap-2 text-navy/80 text-sm">
          <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold capitalize">{order.delivery?.accommodation_type} {order.delivery?.accommodation_name && `— ${order.delivery.accommodation_name}`}</div>
            {order.delivery?.room && <div>Camera: <span className="font-semibold">{order.delivery.room}</span></div>}
            <div>{order.delivery?.address}</div>
            {order.delivery?.instructions && <div className="italic text-navy/60 mt-1">"{order.delivery.instructions}"</div>}
          </div>
        </div>
      </div>

      <div className="mt-4 bg-white rounded-2xl border border-navy/10 p-5">
        <div className="text-xs uppercase tracking-wider text-navy/50 mb-2">Articoli ({order.items?.length || 0})</div>
        {order.items?.map((it, i) => (
          <div key={i} className="flex justify-between text-sm py-1">
            <span>{it.qty}× {it.name}</span>
            <span className="font-semibold">€{(it.price * it.qty).toFixed(2)}</span>
          </div>
        ))}
        <div className="flex justify-between font-bold text-navy pt-2 mt-2 border-t border-navy/10">
          <span>Totale</span><span>€{order.total.toFixed(2)}</span>
        </div>
      </div>

      <div className="fixed bottom-4 left-4 right-4 max-w-lg mx-auto">
        {isFinal ? (
          <Button disabled className="w-full h-16 rounded-full bg-teal text-white text-lg font-bold" data-testid="rider-final-btn">
            <CheckCircle2 className="w-6 h-6 mr-2" /> Consegnata ✓
          </Button>
        ) : nextStatus ? (
          <Button
            onClick={() => advance(nextStatus)}
            disabled={busy}
            className={`w-full h-16 rounded-full text-white text-lg font-bold shadow-2xl ${nextStatus === "delivered" ? "bg-teal hover:bg-teal/90" : "bg-coral hover:bg-coral-hover"}`}
            data-testid={`rider-advance-${nextStatus}`}
          >
            {busy ? "…" : (<>Segna come: {nextLabel} <ArrowRight className="w-5 h-5 ml-2" /></>)}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const Rider = () => {
  const { orderNumber } = useParams();
  return orderNumber ? <RiderDetail orderNumber={orderNumber} /> : <RiderList />;
};

export default Rider;
